import { useEffect, useState, type Dispatch } from 'react';

import type { DraftSet, LoggerAction } from './state/loggerDraftTypes';
import { failToggleFields } from './state/loggerDraftHelpers';
import {
  getEffortIntroSeen,
  loadEffortIntroSeen,
  markEffortIntroSeen,
} from './state/effortMeterIntro';
import { markBandStopSeen } from './state/holdBandStopHint';
import { canonicalRirOf } from './state/canonicalRir';

// Effort-meter commit composition (AP-0b). The meter's tap IS the completion
// gesture, so this hook routes each tap onto the EXISTING legs — it never
// duplicates the completion chain (useSetCyclePill stays the single owner of
// PR check + MAX regression + rest timer + edit-mode placement):
//   pending set  + segment → onLogSet(rir): effort write + full commit leg.
//     …and if a hold/cardio timer is counting on that set, the SAME tap stops
//     it first (captureTimer) and the seconds ride into the same write. That is
//     the whole of MX-5 slice 3: a timed set is completed by the effort meter
//     like every other kind, instead of by a ✓ on a banner that never asked
//     how it felt (AP-0e).
//   pending set  + Fail    → onLogFail(): unchanged fail-commit leg.
//   logged set   + segment → update_set effort EDIT — updates rir (and clears
//     a fail: the band is a radio group, picking an effort means the set WAS
//     completed) but NEVER re-stamps completedAt (timeline integrity,
//     next/timeline.md — same invariant as onToggleFail).
//   logged set   + Fail    → onToggleFail(): flip done ↔ failed in place.
// The first REAL tap ever also opens the effort-scale sheet once (persisted
// flag — see effortMeterIntro) so the band explains itself at the exact
// moment it's first used.

/** Canonical rir of a draft set for the meter's `value`: the rir string when
 *  present, else derived from a legacy rpe (edit/repeat hydration may carry
 *  either — same mirror rule the old effort TextInput used). */
export function draftSetRirValue(set: DraftSet | null): number | null {
  if (!set) return null;
  return canonicalRirOf(set.rir, set.rpe);
}

export function useEffortCommit(args: {
  boundSet: DraftSet | null;
  activeEntryIdx: number;
  boundSetIdx: number;
  dispatch: Dispatch<LoggerAction>;
  /** useTimelineLogger.onLogSet — with the tapped rir it writes the effort
   *  and threads it into cycleSetPill (fresh-rir MAX-regression check). The
   *  second argument carries the held seconds when the tap also stopped a
   *  timer, so both land in ONE update_set. */
  onLogSet: (rir?: number, durationSec?: number) => { prFlash: boolean } | undefined;
  onLogFail: () => void;
  onToggleFail: () => void;
  /** Stops a hold/cardio timer running on the bound set and returns the seconds
   *  held; null when nothing was running (useHoldTimer.capture). Optional — the
   *  card logger and the tests have no timer to stop. */
  captureTimer?: () => number | null;
  /** Opens the EffortScaleModal — reused for the once-ever intro. */
  onOpenEffortScale: () => void;
  /** True when this tap will raise the AMRAP-confirm prompt (LG-26). Used to
   *  HOLD BACK the once-ever intro, never to change what gets written. */
  willPromptAmrap?: (entryIdx: number, setIdx: number) => boolean;
}) {
  const {
    boundSet,
    activeEntryIdx,
    boundSetIdx,
    dispatch,
    onLogSet,
    onLogFail,
    onToggleFail,
    onOpenEffortScale,
    captureTimer,
    willPromptAmrap,
  } = args;

  // PR gold pulse on the tapped segment. Keyed so a repeat PR re-fires; kept
  // here (not derived from set.prFlash) because the commit ADVANCES the
  // binding immediately — by the next render the meter binds the next pending
  // set, so the flash must outlive the tapped set's binding.
  const [flash, setFlash] = useState<{ rir: number; key: number }>({
    rir: -1,
    key: 0,
  });

  useEffect(() => {
    void loadEffortIntroSeen();
  }, []);

  const introOnFirstTap = () => {
    if (getEffortIntroSeen()) return;
    void markEffortIntroSeen();
    onOpenEffortScale();
  };

  const onSelect = (rir: number) => {
    if (activeEntryIdx < 0 || boundSetIdx < 0 || !boundSet) return;
    // Hold the intro back when this same tap is about to raise the AMRAP
    // question (LG-26). Same rule onFail already applies below, and for the
    // same reason: two modals on one gesture is a bug this file has already
    // been fixed for once (Antenore 2026-07-06). The intro is not lost — the
    // flag is only marked when it actually opens, so it fires on the next tap.
    if (!willPromptAmrap?.(activeEntryIdx, boundSetIdx)) introOnFirstTap();
    if (boundSet.completedAt === null) {
      // Stop the count BEFORE committing: the seconds have to be in hand when
      // the write is queued, or they'd need a second update_set behind it (two
      // dispatches for one gesture, and the completion would land on a set
      // whose duration wasn't written yet).
      const heldSec = captureTimer?.() ?? null;
      // They have now stopped a timer with the band once, so the caption that
      // teaches the gesture retires (TM-10). Marked on the DOING, not on the
      // showing.
      if (heldSec !== null) void markBandStopSeen();
      const committed = onLogSet(rir, heldSec ?? undefined);
      if (committed?.prFlash) setFlash((f) => ({ rir, key: f.key + 1 }));
      return;
    }
    // A logged set has no timer running on it (capture cleared it when the set
    // was closed), so the edit leg below never has seconds to fold in.
    // Effort EDIT on a logged set — value change only, completedAt untouched.
    dispatch({
      type: 'update_set',
      entryIdx: activeEntryIdx,
      setIdx: boundSetIdx,
      patch: {
        rir: String(rir),
        rpe: '',
        // Picking an effort segment means the set WAS completed → clear the
        // fail label (AP-0p model). reps are unchanged.
        ...(boundSet.fail ? failToggleFields(false) : {}),
      },
    });
  };

  const onFail = () => {
    if (activeEntryIdx < 0 || boundSetIdx < 0 || !boundSet) return;
    // NO effort-scale intro here (AP-0p): the intro explains the easy→hard
    // BAND, but Fail sits outside it, and a fail on a rep-based set opens the
    // clean-reps prompt — firing both at once stacked two modals (Antenore
    // 2026-07-06). The intro still fires on the first real segment pick.
    if (boundSet.completedAt === null) onLogFail();
    else onToggleFail();
  };

  return {
    // The meter shows RECORDED effort, and now that is all it CAN show.
    //
    // ⛔ The gate that used to stand here (`completedAt == null ? null : …`,
    // 2026-07-06) was a cure for a wound that no longer exists: the plan and
    // the repeat both wrote their prescription into `rpe`/`rir`, so a pending
    // set arrived carrying a value it had not earned, and a filled segment
    // read as "already judged". PE-22 fetta D moved the prescription to its
    // own half (`rirPrescribed`), so a pending set's `rpe`/`rir` are empty by
    // construction and draftSetRirValue returns null on its own. Keeping the
    // gate would hide whether the divorce actually holds — a provisional cure
    // that outlives its replacement stops anyone noticing the replacement
    // broke. The target's own rendering is AP-1 (ghost, never selected).
    value: draftSetRirValue(boundSet),
    failed: boundSet?.fail === true,
    onSelect,
    onFail,
    flashRir: flash.rir,
    flashKey: flash.key,
  };
}
